import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Flag, Pencil } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { ABUSE_REASONS } from "../lib/constants";
import { Textarea } from "./ContactForm";
import { useAuth } from "../context/AuthContext";
import { CancelButton } from "../components/CancelDialog";

export default function ReportAbuse() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ownedCaseId, setOwnedCaseId] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/my/reports").then((res) => {
      const own = res.data.results.find((r) => r.public_case_code === code);
      if (own) setOwnedCaseId(own.id);
    }).catch(() => {});
  }, [user, code]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!reason) { setError("Selecciona un motivo"); return; }
    setSubmitting(true);
    try {
      await api.post(`/cases/${code}/abuse`, { reason, description });
      setDone(true);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-lg mx-auto text-center py-10 space-y-4" data-testid="abuse-success">
        <CheckCircle2 className="w-14 h-14 text-[#047857] mx-auto" />
        <h1 className="font-heading text-2xl font-bold">Reporte enviado</h1>
        <p className="text-[#475569]">
          Tu solicitud no será pública. Será revisada por el equipo autorizado de Reencuentro.
        </p>
        {ownedCaseId ? (
          <div className="space-y-3">
            <p className="text-sm text-[#475569]">Este reporte es tuyo. Puedes corregirlo directamente:</p>
            <Link to={`/mis-reportes/${ownedCaseId}/editar`} data-testid="abuse-edit-own-btn"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#1E3A8A] text-white font-semibold">
              <Pencil className="w-4 h-4" /> Editar mi reporte
            </Link>
          </div>
        ) : (
          <p className="text-sm text-[#475569]">El equipo autorizado revisará la solicitud para corregir, ocultar o investigar el caso si corresponde.</p>
        )}
        <div className="pt-2">
          <Link to={`/caso/${code}`} className="inline-block h-12 px-6 leading-[3rem] rounded-xl bg-[#F1F5F9] text-[#0F172A] font-semibold">Volver al caso</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-5">
      <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><Flag className="w-6 h-6 text-[#991B1B]" /> Reportar abuso</h1>
      <p className="text-sm text-[#475569]">
        Este reporte no será público. Será revisado por el equipo autorizado de Reencuentro para corregir, ocultar o investigar el caso si corresponde.
      </p>
      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-[#475569]">Motivo *</span>
          <select data-testid="abuse-reason" value={reason} onChange={(e) => setReason(e.target.value)}
            className="mt-1 w-full h-12 px-3 rounded-lg border border-[#E2E8F0] bg-white">
            <option value="">Selecciona un motivo</option>
            {ABUSE_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </label>
        <Textarea label="Descripción (opcional)" testid="abuse-description" value={description} onChange={setDescription} />
        {error && <p className="text-sm text-[#DC2626]" data-testid="abuse-error">{error}</p>}
        <div className="flex gap-3">
          <button type="button" onClick={() => navigate(-1)} data-testid="abuse-back-btn"
            className="h-14 px-5 rounded-xl bg-[#F1F5F9] text-[#0F172A] font-semibold">Volver</button>
          <button type="submit" disabled={submitting} data-testid="abuse-submit-btn"
            className="flex-1 h-14 rounded-xl bg-[#991B1B] text-white font-semibold hover:bg-[#7F1D1D] disabled:opacity-50">
            {submitting ? "Enviando…" : "Enviar reporte"}
          </button>
        </div>
        <CancelButton testid="abuse-cancel-btn" onConfirm={() => navigate("/")} />
      </form>
    </div>
  );
}
